type CachedNode<Node, Dependencies extends unknown[], Result> = {
  node: Node;
  dependencies: Dependencies;
  result: Result;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

// A plain-object dependency bundles several inputs into one argument, so it is compared
// field by field. Everything else is compared by identity.
function isSameDependency(previous: unknown, next: unknown): boolean {
  if (Object.is(previous, next)) {
    return true;
  }

  if (!isPlainObject(previous) || !isPlainObject(next)) {
    return false;
  }

  const previousKeys = Object.keys(previous);

  return (
    previousKeys.length === Object.keys(next).length &&
    previousKeys.every(key => Object.is(previous[key], next[key]))
  );
}

function haveSameDependencies(previous: unknown[], next: unknown[]): boolean {
  return (
    previous.length === next.length &&
    previous.every((dependency, index) =>
      isSameDependency(dependency, next[index])
    )
  );
}

export function memoizeByNode<
  Node extends { id: string },
  Dependencies extends unknown[],
  Result,
>(
  createNode: (node: Node, ...dependencies: Dependencies) => Result
): (node: Node, ...dependencies: Dependencies) => Result {
  const cache = new Map<string, CachedNode<Node, Dependencies, Result>>();

  return (node, ...dependencies) => {
    const cached = cache.get(node.id);

    if (
      cached &&
      Object.is(cached.node, node) &&
      haveSameDependencies(cached.dependencies, dependencies)
    ) {
      return cached.result;
    }

    const result = createNode(node, ...dependencies);

    cache.set(node.id, { node, dependencies, result });

    return result;
  };
}
